import type { FC } from 'react';

import { useRouter } from 'next/compat/router';
import { useCallback, useEffect, useRef } from 'react';

import { getTopLevelNavigationEnabled } from '~/components/shared/header/topLevelNavigation/hooks/useTopLevelNavigationEnabled';
import type { GlobalTopLevelNavigationType } from '~/types/amplience';
import { useUserContext } from '~/utilities/context/dynamic/UserContext';
import { useAppContext } from '~/utilities/context/static/AppContext';
import { useDataLayerContext } from '~/utilities/context/static/DataLayerContext';
import type { CommonData, Configuration, Country } from '~/utilities/graphql/codegen';
import {
  parsePageInfoDataLayer,
  parseTopNavigationExperienceImpression,
  parseUserInfoDataLayer,
} from '~/utilities/vendors';

const DATA_POLLING_INTERVAL_MS = 200;
const MAX_POLLING_ATTEMPTS = 150; // 30 seconds max (150 * 200ms)

type SpaPageLoadProps = {
  configuration: Configuration;
  countryEn: Country;
  topLevelNavigationContent?: GlobalTopLevelNavigationType;
};

export const SpaPageLoad: FC<SpaPageLoadProps> = ({
  configuration,
  countryEn,
  topLevelNavigationContent,
}) => {
  const router = useRouter();
  const {
    slice,
    defaultCurrency,
    enableCMP,
    cmpDomainId,
    experienceImpressionEventEnabled,
    topNavigationEnabled,
    topNavigationMobileEnabled,
  } = configuration;
  // TK69 variant follows what the site renders, the same rule as the header
  const { desktop: topNavigationDesktop, mobile: topNavigationMobile } =
    getTopLevelNavigationEnabled(
      { topNavigationEnabled, topNavigationMobileEnabled },
      topLevelNavigationContent?.categories
    );
  const { locale, language, pageType } = useAppContext();
  const { commonData } = useUserContext();
  const { pushToDataLayer } = useDataLayerContext();

  const isInitialTriggered = useRef<boolean>(false);
  const timeoutRef = useRef<number>(0);
  const commonDataRef = useRef<CommonData | null | undefined>(commonData);
  const attemptCountRef = useRef<number>(0);

  const isCmpActive = Boolean(enableCMP && cmpDomainId);

  commonDataRef.current = commonData;

  const handleSPAEvents = useCallback(
    (callback?: () => void) => {
      /**
       * commonData may not be initially available, it needs pulling so we don't lose datalayer events
       */
      function timeoutCallback() {
        const dataLayerReady = Boolean(commonDataRef.current && language && locale && countryEn);

        if (dataLayerReady) {
          const pageInfo = parsePageInfoDataLayer(
            locale,
            language,
            slice,
            defaultCurrency,
            countryEn,
            pageType
          );
          const userInfo = parseUserInfoDataLayer({ commonData: commonDataRef.current });

          pushToDataLayer({ event: 'spa_page_load', page: pageInfo, user: userInfo }, true);

          // after `spa_page_load`, so it is attributed to the page just landed on. Never sent on
          // PDV (product detail view in the mobile app webview, no navigation at all): its props
          // nest the configuration under `static`, so `_app` hands this component `{}`.
          const experienceImpression = parseTopNavigationExperienceImpression(
            experienceImpressionEventEnabled,
            { desktop: topNavigationDesktop, mobile: topNavigationMobile }
          );

          if (experienceImpression) {
            pushToDataLayer(experienceImpression);
          }

          clearTimeout(timeoutRef.current);
          attemptCountRef.current = 0;

          if (typeof callback === 'function') {
            callback?.();
          }
        } else {
          attemptCountRef.current += 1;

          if (attemptCountRef.current >= MAX_POLLING_ATTEMPTS) {
            console.error(
              'SpaPageLoad: Failed to get required data after maximum polling attempts'
            );
            clearTimeout(timeoutRef.current);
            attemptCountRef.current = 0;

            return;
          }

          timeoutRef.current = window.setTimeout(() => timeoutCallback(), DATA_POLLING_INTERVAL_MS);
        }
      }

      attemptCountRef.current = 0;
      timeoutCallback();
    },
    [
      language,
      countryEn,
      slice,
      defaultCurrency,
      pageType,
      pushToDataLayer,
      locale,
      experienceImpressionEventEnabled,
      topNavigationDesktop,
      topNavigationMobile,
    ]
  );

  useEffect(() => {
    const dispatchInitialEvent = () => {
      if (!isInitialTriggered.current) {
        handleSPAEvents(() => {
          isInitialTriggered.current = true;
        });
      }
    };

    if (isInitialTriggered.current) {
      return () => {};
    }

    if (!isCmpActive) {
      dispatchInitialEvent();

      return () => {};
    }

    /**
     * the `OneTrustGroupsUpdated` event can trigger before `commonData` is available
     */
    window.addEventListener('OneTrustGroupsUpdated', dispatchInitialEvent);

    return () => {
      window.removeEventListener('OneTrustGroupsUpdated', dispatchInitialEvent);
    };
  }, [handleSPAEvents, isCmpActive]);

  useEffect(() => {
    if (!router) {
      return () => {};
    }

    router.events.on('routeChangeComplete', handleSPAEvents);

    return () => {
      router.events.off('routeChangeComplete', handleSPAEvents);
    };
  }, [handleSPAEvents, router]);

  /**
   * clear timeout regarding the `isCmpActive` value
   */
  useEffect(
    () => () => {
      clearTimeout(timeoutRef.current);
      attemptCountRef.current = 0;
    },
    []
  );

  return null;
};
